import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Col, Divider, Row, Skeleton, Space, Tabs } from "antd";
import BreadCrumb from "../../../components/BreadCrumb/BreadCrumb";
import PageTitleNested from "../../../components/PageTitle/PageTitleNested";
import { CardContainer, OuterCardContainer } from "../../../components/Card/CardContainer";
import Text from "../../../components/Text/Text";
import OrderTab from "./TabOrderProduct/OrderTab";
import ProductTab from "./TabOrderProduct/ProductTab";
import CardMileStone from "../../../components/Card/CardMileStone";
import {
  getAchieveShopDetail,
  getAchieveShopDetailOrderProd,
} from "../../../datasource/AchievementDataSource";
import { useEffectOnce } from "react-use";
import {
  productDetailAchieveEntity_INIT,
  shopDetailOrderEntity_INIT,
  type productDetailAchieveEntity,
  type shopDetailAchieveEntity,
  type shopDetailOrderEntity,
} from "../../../entities/AchievementEntity";
import { formatDateThai } from "../../../utility/dateHelpers";
import { useTabState } from "../../../hook/useTabState";

function ShopDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const defaultShop = location.state?.shop;
  const [loading, setLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  type TabKey = "ORDER" | "PRODUCT";
  const company = JSON.parse(localStorage.getItem("company")!);
  const [activeTab, setActiveTab] = useState<TabKey>("ORDER");
  const [detail, setDetail] = useState<shopDetailAchieveEntity>();
  const [orderData, setOrderData] = useState<{ data: shopDetailOrderEntity[]; count: number }>({
    data: [shopDetailOrderEntity_INIT],
    count: 0,
  });
  const [prodData, setProdData] = useState<{ data: productDetailAchieveEntity[]; count: number }>({
    data: [productDetailAchieveEntity_INIT],
    count: 0,
  });
  const orderTabState = useTabState();
  const productTabState = useTabState();
  const fetchDetail = async () => {
    setLoading(true);
    try {
      await getAchieveShopDetail({
        achievementId: id,
        company: company?.companyCode,
        customerCompanyId: defaultShop?.customerCompanyId,
      }).then((res) => {
        setDetail(res);
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const fetchData = async () => {
    setOrderLoading(true);
    try {
      let params: any = {
        achievementId: id,
        company: company?.companyCode,
        customerCompanyId: defaultShop?.customerCompanyId,
        type: activeTab,
        take: 10,
      };

      if (activeTab === "ORDER") {
        params = {
          ...params,
          status: orderTabState.status,
          searchText: orderTabState.searchText,
          sortBy: orderTabState.sortField,
          sortDirection: orderTabState.sortDirection,
          page: orderTabState.page,
        };
      } else {
        params = {
          ...params,
          searchText: productTabState.searchText,
          sortBy: productTabState.sortField,
          sortDirection: productTabState.sortDirection,
          page: productTabState.page,
        };
      }

      const res = await getAchieveShopDetailOrderProd(params as any);
      activeTab === "ORDER" ? setOrderData(res) : setProdData(res);
    } catch (error) {
      console.error("fetchData error:", error);
    } finally {
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    activeTab,
    orderTabState.searchText,
    orderTabState.status,
    orderTabState.sortField,
    orderTabState.sortDirection,
    orderTabState.page,
    productTabState.searchText,
    productTabState.sortField,
    productTabState.sortDirection,
    productTabState.page,
  ]);
  useEffect(() => {
    if (activeTab === "ORDER") {
      productTabState.reset();
    } else if (activeTab === "PRODUCT") {
      orderTabState.reset();
    }
  }, [activeTab]);

  useEffectOnce(() => {
    fetchDetail();
  });

  const PageTitle = () => (
    <PageTitleNested
      title={"ร้านค้า: " + defaultShop?.customerName}
      showBack
      customBreadCrumb={
        <BreadCrumb
          data={[
            { text: "รายการความสำเร็จ", path: "/crm/achievement" },
            { text: "รายละเอียดความสำเร็จ", path: `/crm/achievement/${id}` },
            { text: "รายละเอียดร้านค้า", path: window.location.pathname },
          ]}
        />
      }
    />
  );

  const details: any[] = [
    { label: "รหัสความสำเร็จ", value: `${detail?.achievementCode}` },
    { label: "ชื่อความสำเร็จ", value: `${detail?.achievementName}` },
    {
      label: "ระยะเวลาแคมเปญ",
      value: `${detail?.startDate && formatDateThai(new Date(detail?.startDate))} น. - ${detail?.endDate && formatDateThai(new Date(detail?.endDate))} น.`,
    },
    {
      label: "เงื่อนไขความสำเร็จ",
      value: `ยอดซื้อสะสม ${detail?.conditionsToAchieve === "THB" ? "(บาท)" : "(จำนวน)"}`,
    },
    {
      label: "เงื่อนไขการรับรางวัล",
      value: `${detail?.receiveRewardType === "STEP" ? "รับรางวัลแบบขั้นบันได (รางวัลสูงสุด)" : "รับรางวัลของทุกๆระดับ (ทุกรางวัลที่สำเร็จ)"}`,
    },
  ];
  const detailShop: any[] = [
    { label: "รหัสร้านค้า", value: `${detail?.customerNo}` },
    { label: "ชื่อร้านค้า", value: `${detail?.customerName}` },
    { label: "ประเภทคู่ค้า", value: `${detail?.customerType}` },
  ];

  const tabsData = [
    {
      key: "ORDER",
      label: <Text fontSize={14}>รายการคำสั่งซื้อ</Text>,
      children: (
        <OrderTab
          data={orderData}
          sortField={orderTabState.sortField}
          sortDirection={orderTabState.sortDirection}
          handleSortChange={orderTabState.handleSortChange}
          searchText={orderTabState.searchText}
          setSearchText={orderTabState.setSearchText}
          status={orderTabState.status}
          setStatus={orderTabState.setStatus}
          page={orderTabState.page}
          setPage={orderTabState.setPage}
          loading={orderLoading}
        />
      ),
    },
    {
      key: "PRODUCT",
      label: <Text fontSize={14}>ยอดสินค้าที่สั่งซื้อ</Text>,
      children: (
        <ProductTab
          data={prodData}
          searchText={productTabState.searchText}
          setSearchText={productTabState.setSearchText}
          sortField={productTabState.sortField}
          sortDirection={productTabState.sortDirection}
          handleSortChange={productTabState.handleSortChange}
          page={productTabState.page}
          setPage={productTabState.setPage}
          loading={orderLoading}
        />
      ),
    },
  ];

  return (
    <OuterCardContainer>
      <CardContainer cutBottom>
        <PageTitle />
      </CardContainer>
      <Divider />

      <div style={{ padding: "0px 24px" }}>
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Text fontSize={16} fontWeight={700}>
              ข้อมูลความสำเร็จที่เข้าร่วม
            </Text>
            {details.map((item, index) =>
              loading ? (
                <Skeleton title paragraph={false} active key={index} />
              ) : (
                <Row key={index} style={{ margin: "12px 0px 6px 0px" }}>
                  <Col span={6}>
                    <Space align='center'>
                      <Text fontFamily='Sarabun' fontSize={16} color='Text3'>
                        {item.label}
                      </Text>
                    </Space>
                  </Col>
                  <Col span={18}>
                    <Text fontFamily='Sarabun' fontSize={16}>
                      {item.value}
                    </Text>
                  </Col>
                </Row>
              ),
            )}
          </Col>

          <Col span={12}>
            <Text fontSize={16} fontWeight={700}>
              ข้อมูลร้านค้า
            </Text>
            {detailShop.map((item, index) =>
              loading ? (
                <Skeleton title paragraph={false} active key={index} />
              ) : (
                <Row key={index} style={{ margin: "12px 0px 6px 0px" }}>
                  <Col span={6}>
                    <Space align='center'>
                      <Text fontFamily='Sarabun' fontSize={16} color='Text3'>
                        {item.label}
                      </Text>
                    </Space>
                  </Col>
                  <Col span={18}>
                    <Text fontFamily='Sarabun' fontSize={16}>
                      {item.value}
                    </Text>
                  </Col>
                </Row>
              ),
            )}
          </Col>
        </Row>

        <div style={{ margin: "22px 0px" }}>
          <CardMileStone
            img={detail?.currentBadgeMasterUrl}
            mainReward={detail?.mainReward}
            specialReward={detail?.specialReward}
            progressValue={detail?.progressValue}
            remainingToNext={detail?.remainingToNext}
            currentMilestoneName={
              detail?.isFinishMain && detail.currentMilestoneSpecialName
                ? detail.currentMilestoneSpecialName
                : detail?.currentMilestoneName
            }
            currentRewardNum={
              detail?.isFinishMain && detail.currentSpecialRewardNum
                ? detail.currentSpecialRewardNum
                : detail?.currentRewardNum
            }
            label={
              detail?.isFinishMain && detail.currentSpecialRewardNum ? "รางวัลพิเศษที่" : "ระดับที่"
            }
          />
        </div>

        <Tabs
          items={tabsData}
          activeKey={activeTab}
          onChange={(tab) => {
            setActiveTab(tab as TabKey);
          }}
        />
      </div>
    </OuterCardContainer>
  );
}

export default ShopDetailPage;
